import * as THREE from "three";

export type PowerKind = "freeze" | "mult";

export interface PowerPickup {
  kind: PowerKind;
  position: THREE.Vector3;
}

interface Orb {
  mesh: THREE.Mesh;
  inner: THREE.Mesh;
  kind: PowerKind;
  picked: boolean;
}

const ORB_RADIUS = 0.45;
const PICKUP_LATERAL = 1.8;
// Min/max seconds between spawn attempts. Drift random keeps power-ups
// from arriving on a predictable cadence.
const SPAWN_MIN_S = 8;
const SPAWN_MAX_S = 14;

/**
 * Spawns drifting power-up orbs along the ship's flight path. Two kinds:
 *   - freeze (cyan): pauses time burn for 3s.
 *   - mult   (magenta): doubles multiplier for 5s.
 *
 * Orbs are world-stationary once spawned (placed ahead of the ship and the
 * ship flies into them, mirroring how rings are encountered). Hit-test on
 * z-pass + lateral distance.
 */
export class PowerUps {
  readonly group = new THREE.Group();
  private orbs: Orb[] = [];
  private rng: () => number;
  private nextSpawnInS = 0;
  private elapsed = 0;

  constructor(seed = 0) {
    this.rng = makeRng(seed);
    this.scheduleNext(true);
  }

  reset(seed: number) {
    this.rng = makeRng(seed);
    this.elapsed = 0;
    this.nextSpawnInS = 0;
    for (const o of this.orbs) this.dispose(o);
    this.orbs.length = 0;
    this.scheduleNext(true);
  }

  /**
   * Per-frame update. `shipPos` drives collision + culling.
   * Returns any pickups triggered this frame.
   */
  update(dt: number, shipPos: THREE.Vector3): PowerPickup[] {
    this.elapsed += dt;
    const events: PowerPickup[] = [];

    if (this.elapsed >= this.nextSpawnInS) {
      this.spawn(shipPos);
      this.scheduleNext();
    }

    for (const o of this.orbs) {
      if (o.picked) continue;
      o.mesh.rotation.y += dt * 1.5;
      o.mesh.rotation.x += dt * 0.7;
      // Pickup: ship passed the orb's z (ship.z moves -z direction) AND
      // is laterally close enough.
      if (shipPos.z <= o.mesh.position.z + 0.3) {
        const dx = o.mesh.position.x - shipPos.x;
        const dy = o.mesh.position.y - shipPos.y;
        if (Math.hypot(dx, dy) < PICKUP_LATERAL) {
          o.picked = true;
          events.push({ kind: o.kind, position: o.mesh.position.clone() });
          this.dispose(o);
        }
      }
    }

    // Cull orbs that the ship has flown well past.
    for (let i = this.orbs.length - 1; i >= 0; i--) {
      const o = this.orbs[i];
      if (o.picked) {
        this.orbs.splice(i, 1);
      } else if (o.mesh.position.z > shipPos.z + 18) {
        this.dispose(o);
        this.orbs.splice(i, 1);
      }
    }

    return events;
  }

  private spawn(shipPos: THREE.Vector3) {
    // Place ~80u ahead of the ship, in the same lateral spread as rings.
    const z = shipPos.z - 80 - this.rng() * 40;
    const x = (this.rng() - 0.5) * 14;
    const y = (this.rng() - 0.5) * 6;
    const kind: PowerKind = this.rng() < 0.55 ? "freeze" : "mult";

    const color = kind === "freeze" ? 0x66e0ff : 0xff66ee;
    const outerGeo = new THREE.IcosahedronGeometry(ORB_RADIUS, 1);
    const outerMat = new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: 1.8,
      metalness: 0.5,
      roughness: 0.3,
      transparent: true,
      opacity: 0.95,
    });
    const mesh = new THREE.Mesh(outerGeo, outerMat);
    mesh.position.set(x, y, z);

    // Inner halo billboard for visibility against bright nebulae.
    const innerGeo = new THREE.SphereGeometry(ORB_RADIUS * 1.7, 16, 12);
    const innerMat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.18,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const inner = new THREE.Mesh(innerGeo, innerMat);
    mesh.add(inner);

    this.group.add(mesh);
    this.orbs.push({ mesh, inner, kind, picked: false });
  }

  private dispose(o: Orb) {
    this.group.remove(o.mesh);
    o.mesh.geometry.dispose();
    (o.mesh.material as THREE.Material).dispose();
    o.inner.geometry.dispose();
    (o.inner.material as THREE.Material).dispose();
  }

  private scheduleNext(now = false) {
    const dur = SPAWN_MIN_S + this.rng() * (SPAWN_MAX_S - SPAWN_MIN_S);
    this.nextSpawnInS = (now ? 4 : this.elapsed) + dur;
  }
}

function makeRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
